import React from "react";
import { LoadingIndicator } from "@jsq/ds/core";
import {
  usePositionWorkflowPaymentPrefsQuery,
  usePositionWorkflowSupportedPaymentMethodsQuery,
} from "../../../../graphql/supergraph.index";
import { WorkflowMode } from "../../common/enums";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { SelectAvailablePaymentPrefsForm } from "../../Components/SelectAvailablePaymentPrefsForm";
import { SelectPositionsToUpdate } from "../../Components/SelectPositionsToUpdate";
import { ChildComponentProps } from "../../Components/types";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { getAvailablePaymentMethods, getSingleAvailablePaymentMethod } from "../../mappers";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";
import { PaymentPrefCreationForPositionWorkflowProps } from "./types";

/**
 * A workflow component that handles the creation of payment preferences for a specific position.
 * It manages the data fetching and form presentation for creating new payment preferences.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {Position} props.position - Position details for the workflow
 * @param {string} [props.sourcePaymentPrefId] - Optional ID of source payment preference for switching
 * @param {Arena} [props.arena] - Arena configuration object that determines which mutation to call based on hasPositionPaymentPrefStaffApprovals feature flag
 *
 * @example
 * ```tsx
 * <PaymentPrefCreationForPositionWorkflow
 *   onExit={() => setShowWorkflow(false)}
 *   onSuccess={handleSuccess}
 *   position={{
 *     id: '123',
 *     accountId: '456',
 *     investmentEntityId: '789'
 *   }}
 *   sourcePaymentPrefId="101112"
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 * ```
 *
 * @remarks
 * - Uses ConsolidatedPaymentPrefProvider for context management
 * - Handles loading and error states
 * - Supports switching from existing payment preferences
 * - Integrates with BasePaymentPrefWorkflow for form presentation
 */
export const PaymentPrefCreationForPositionWorkflow: React.FC<
  PaymentPrefCreationForPositionWorkflowProps
> = ({ onExit, onSuccess, position, sourcePaymentPrefId, arena }) => {
  // Load existing payment prefs for the account
  const {
    data: positionWorkflowPaymentPrefsData,
    loading: positionWorkflowPaymentPrefsLoading,
    error: positionWorkflowPaymentPrefsError,
  } = usePositionWorkflowPaymentPrefsQuery({
    variables: { accountId: position.accountId },
  });
  const existingPaymentPrefs = positionWorkflowPaymentPrefsData?.account?.paymentPrefs;

  // We are either creating a payment pref for a position without one or switching
  // the payment pref for a position to a new one.
  const isSwitching = Boolean(sourcePaymentPrefId && sourcePaymentPrefId.length > 0);

  let prefixComponents: React.FC<ChildComponentProps>[] = [];
  /**
   * If there are existing payment prefs then we must allow them to be selected.
   *
   * If there is only existing payment pref and we're switching from it,
   * then there's no need to display the select available payment prefs component.
   * See https://junipersquare.atlassian.net/browse/FF-3344 for more details.
   */
  if (existingPaymentPrefs && existingPaymentPrefs.length > (isSwitching ? 1 : 0)) {
    prefixComponents = [SelectAvailablePaymentPrefsForm];
  }

  // Load supported payment methods for the investment entity
  const {
    data: positionWorkflowSupportedPaymentMethodsData,
    loading: positionWorkflowSupportedPaymentMethodsLoading,
    error: positionWorkflowSupportedPaymentMethodsError,
  } = usePositionWorkflowSupportedPaymentMethodsQuery({
    variables: { entityId: position.investmentEntityId },
  });

  if (
    positionWorkflowPaymentPrefsLoading ||
    positionWorkflowSupportedPaymentMethodsLoading ||
    positionWorkflowSupportedPaymentMethodsError ||
    positionWorkflowPaymentPrefsError ||
    !positionWorkflowSupportedPaymentMethodsData?.investmentEntity?.supportedPaymentMethods
  ) {
    return <LoadingIndicator />;
  }

  // Get the available payment methods for the related investment entity
  const availablePaymentMethods = getAvailablePaymentMethods({
    availablePaymentMethods:
      positionWorkflowSupportedPaymentMethodsData.investmentEntity.supportedPaymentMethods,
  });

  const singleAvailablePaymentMethod = getSingleAvailablePaymentMethod(availablePaymentMethods);

  return (
    <ConsolidatedPaymentPrefProvider
      availablePaymentMethods={availablePaymentMethods}
      singleAvailablePaymentMethod={singleAvailablePaymentMethod}
      sourcePaymentPrefId={sourcePaymentPrefId}
      currentPosition={position}
      existingPaymentPrefs={existingPaymentPrefs}
      workflowMode={WorkflowMode.Create}
      accountName={positionWorkflowPaymentPrefsData?.account?.name}
      arena={arena}
    >
      <BasePaymentPrefWorkflow
        workflowMode={WorkflowMode.Create}
        prefixComponents={prefixComponents}
        suffixComponents={[SelectPositionsToUpdate, PaymentPrefConfirmation]}
        onExit={onExit}
        onSuccess={onSuccess}
      />
    </ConsolidatedPaymentPrefProvider>
  );
};
